/**
 * Tickets — master-detail layout.
 *
 * Desktop (≥ lg): two columns. Left = filterable ticket list (~360 px),
 * right = inline detail panel with tabs (Übersicht / Kommentare /
 * Anruf-Log). Selecting a row swaps the panel without leaving the page.
 *
 * Mobile (< lg): single column. The list shows by default; tapping a
 * row pushes the detail full-bleed with a "Zurück zur Liste" affordance.
 *
 * The detail panel reads the `callId` off the ticket and lazily fetches
 * the call transcript via the existing `useCall` / `useCallEvents`
 * hooks — no extra round-trips for tickets that aren't voice-AI created.
 */
import * as React from 'react';
import {
  Loader2,
  Plus,
  AlertTriangle,
  Phone,
  Clock,
  Search,
  Check,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useUser } from '../../auth/AuthProvider';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  tsToDate,
  useTickets,
  type TicketRow,
  type TicketStatus,
} from '../../hooks/useTickets';
import type { ListTicketsInput } from '@cp3/shared';
import { cn } from '../../lib/utils';
import { CreateTicketDialog } from './CreateTicketDialog';
import { TicketDetailPanel } from './TicketDetailPanel';
import { HeaderActions } from '../../components/HeaderActions';

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Offen',
  'in-progress': 'In Bearbeitung',
  'waiting-customer': 'Warten auf Kunde',
  deferred: 'Zurückgestellt',
  resolved: 'Gelöst',
  closed: 'Geschlossen',
};

type StatusFilter = 'all' | 'active' | 'overdue' | TicketStatus;

export function TicketsPage() {
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('active');
  const [search, setSearch] = React.useState('');
  const [showCreate, setShowCreate] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { isUnread, markRead } = useReadTickets();

  const filters = React.useMemo<ListTicketsInput>(() => {
    const f: ListTicketsInput = {};
    if (search.trim()) f.search = search.trim();
    if (statusFilter === 'active') {
      f.status = ['open', 'in-progress', 'waiting-customer'];
    } else if (statusFilter === 'overdue') {
      f.status = ['open', 'in-progress', 'waiting-customer'];
    } else if (statusFilter !== 'all') {
      f.status = [statusFilter];
    }
    return f;
  }, [statusFilter, search]);

  const tickets = useTickets(filters);

  const filtered = React.useMemo(() => {
    if (!tickets.data) return [];
    if (statusFilter !== 'overdue') return tickets.data;
    const now = Date.now();
    return tickets.data.filter((t) => {
      const due = tsToDate(t.dueAt);
      return due && due.getTime() < now;
    });
  }, [tickets.data, statusFilter]);

  // Auto-select the first ticket in the visible list once the data
  // arrives — but never overwrite an existing user choice. We also
  // un-select if the chosen ticket left the filtered set (e.g. status
  // change moved it out of "Aktiv").
  React.useEffect(() => {
    if (!filtered.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && filtered.some((t) => t.id === prev)) {
        markRead(prev);
        return prev;
      }
      const next = filtered[0]?.id ?? null;
      if (next) markRead(next);
      return next;
    });
  }, [filtered, markRead]);

  const selected = React.useMemo(
    () => filtered.find((t) => t.id === selectedId) ?? null,
    [filtered, selectedId],
  );

  return (
    // The AppShell renders this page in a flush frame (no padding, no
    // gap to the SiteHeader). The master-detail grid spans the full
    // viewport below the title bar — no card, no rounded outer border,
    // no inner gap — exactly as the user asked.
    <div className="flex min-h-0 flex-1 flex-col">
      <HeaderActions>
        <div className="hidden items-center gap-2 lg:flex">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Anfrage suchen"
              className="h-8 w-[220px] pl-8 text-sm"
              aria-label="Anfragen durchsuchen"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Aktive Anfragen</SelectItem>
              <SelectItem value="overdue">Überfällig</SelectItem>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="resolved">Gelöst</SelectItem>
              <SelectItem value="closed">Geschlossen</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[11px] text-muted-foreground">
            {tickets.isLoading ? 'Lade …' : `${filtered.length}`}
          </span>
        </div>
        <Button size="sm" className="h-8" onClick={() => setShowCreate(true)}>
          <Plus className="size-4" />
          <span className="ml-2 hidden sm:inline">Neue Anfrage</span>
        </Button>
      </HeaderActions>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(320px,380px)_1fr]">
          {/* ─── List column ─────────────────────────────────────────── */}
          <aside
            className={cn(
              'flex min-h-0 flex-col border-border lg:border-r',
              // On mobile, hide the list entirely while a ticket is open
              // so the detail gets full-bleed real estate.
              selected ? 'hidden lg:flex' : 'flex',
            )}
          >
            {/* Mobile-only filter bar — desktop renders identical
                controls in HeaderActions, so we hide this row on lg+. */}
            <div className="lg:hidden">
              <FilterBar
                search={search}
                setSearch={setSearch}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                count={filtered.length}
                loading={tickets.isLoading}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {tickets.isLoading ? (
                <div className="flex h-32 items-center justify-center text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Tickets …
                </div>
              ) : filtered.length === 0 ? (
                <div className="m-3 rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  Hier ist gerade alles ruhig. Sobald neue Anfragen reinkommen,
                  findest du sie hier.
                </div>
              ) : (
                <ul className="flex flex-col gap-2 p-2">
                  {filtered.map((t) => (
                    <li key={t.id}>
                      <TicketRowItem
                        ticket={t}
                        selected={selectedId === t.id}
                        unread={isUnread(t.id)}
                        onSelect={() => {
                          setSelectedId(t.id);
                          markRead(t.id);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          {/* ─── Detail column ───────────────────────────────────────── */}
          <section
            className={cn(
              'flex min-h-0 flex-col bg-background',
              selected ? 'flex' : 'hidden lg:flex',
            )}
          >
            {selected ? (
              <TicketDetailPanel
                key={selected.id}
                ticket={selected}
                onBack={() => setSelectedId(null)}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                Wähle eine Anfrage aus und du siehst alles auf einen Blick. Den
                Inhalt, eure Kommentare und den Anruf-Verlauf.
              </div>
            )}
          </section>
        </div>
      </div>

      {showCreate && <CreateTicketDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}

// ── Filter bar (mobile only) ───────────────────────────────────────────

interface FilterBarProps {
  search: string;
  setSearch: (s: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (s: StatusFilter) => void;
  count: number;
  loading: boolean;
}

/**
 * Compact mobile filter row — search field + a single status select.
 * Priority + category were dropped to keep the phone view scannable;
 * they're still editable inside each ticket's detail panel.
 */
function FilterBar({
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  count,
  loading,
}: FilterBarProps) {
  return (
    <div className="space-y-2 border-b border-border bg-card/40 px-3 py-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Anfrage suchen"
          className="h-10 rounded-xl pl-10 text-sm"
        />
      </div>
      <div className="flex items-center gap-2">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="h-9 flex-1 rounded-xl text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Aktive Anfragen</SelectItem>
            <SelectItem value="overdue">Überfällig</SelectItem>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="resolved">Gelöst</SelectItem>
            <SelectItem value="closed">Geschlossen</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {loading ? 'Lade …' : `${count}`}
        </span>
      </div>
    </div>
  );
}

// ── List row ───────────────────────────────────────────────────────────

/**
 * Card-style ticket tile inspired by the requested mock-up. Each row
 * is a self-contained rounded surface with:
 *   • code + (optional) unread dot
 *   • subject (bold, mobile primary)
 *   • description preview (one line)
 *   • caller name + phone (mobile-essential contact info)
 *   • status badge + due-date footer
 *   • a checkmark in the top-right when this card is the selected one
 *   • a red left bar + red tint for high-priority / urgent / overdue
 */
function TicketRowItem({
  ticket,
  selected,
  unread,
  onSelect,
}: {
  ticket: TicketRow;
  selected: boolean;
  unread: boolean;
  onSelect: () => void;
}) {
  const due = tsToDate(ticket.dueAt);
  const created = tsToDate(ticket.createdAt);
  const overdue =
    due != null
    && due.getTime() < Date.now()
    && ticket.status !== 'resolved'
    && ticket.status !== 'closed';
  const needsAttention =
    overdue || ticket.priority === 'high' || ticket.priority === 'urgent';
  const stamp = due ?? created;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'relative w-full overflow-hidden rounded-2xl border bg-card p-4 text-left transition-all',
        'hover:border-primary/40 hover:bg-card/80 hover:shadow-sm',
        selected
          ? 'border-primary/60 bg-primary/5 shadow-sm ring-1 ring-primary/40'
          : 'border-border',
        needsAttention && !selected && 'border-destructive/40 bg-destructive/5',
      )}
      aria-current={selected ? 'true' : undefined}
    >
      {/* Red attention bar — full card height. */}
      {needsAttention && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-1 bg-destructive"
        />
      )}

      {/* Selected checkmark — mirrors the right-aligned tick from the
          design mock-up. */}
      {selected && (
        <span
          aria-hidden
          className="absolute right-3 top-3 inline-flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow"
        >
          <Check className="size-3" />
        </span>
      )}

      <div className="flex items-center gap-1.5 pr-7">
        {unread && !selected && (
          <span
            aria-hidden
            className="size-1.5 shrink-0 rounded-full bg-primary"
            title="Ungelesen"
          />
        )}
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {ticket.publicCode}
        </span>
        {ticket.escalationLevel > 0 && (
          <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-400">
            L{ticket.escalationLevel}
          </span>
        )}
      </div>

      <h3 className="mt-0.5 truncate text-[15px] font-semibold leading-tight pr-7">
        {ticket.subject}
      </h3>

      {ticket.description && (
        <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
          {ticket.description}
        </p>
      )}

      {/* Caller / phone — mobile essentials. We keep them on a single
          row and let truncation hide overflow. */}
      {(ticket.callerName || ticket.callerPhone) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-foreground/80">
          {ticket.callerName && <span className="font-medium">{ticket.callerName}</span>}
          {ticket.callerPhone && (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Phone className="size-2.5" /> {ticket.callerPhone}
            </span>
          )}
        </div>
      )}

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <StatusBadge status={ticket.status} />
          {overdue && (
            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-destructive">
              <AlertTriangle className="size-2.5" /> überfällig
            </span>
          )}
        </div>
        {stamp && (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-[10px] tabular-nums text-muted-foreground',
              overdue && 'text-destructive',
            )}
          >
            <Clock className="size-2.5" />
            {stamp.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        )}
      </div>
    </button>
  );
}

// ── Read/unread tracking ────────────────────────────────────────────────

/**
 * Persists "ticket has been opened" state in localStorage, scoped per
 * signed-in user. The list shows a small dot next to each ticket the
 * current user hasn't yet clicked; the dot disappears the moment they
 * select the row. State survives full reloads but is cleared on sign-out
 * because the storage key is bound to the auth uid.
 */
function useReadTickets(): {
  isUnread: (id: string) => boolean;
  markRead: (id: string) => void;
} {
  const { user } = useUser();
  const uid = user?.uid;
  const storageKey = uid ? `cp3:tickets:read:${uid}` : null;
  const [readSet, setReadSet] = React.useState<Set<string>>(() => loadReadSet(storageKey));

  React.useEffect(() => {
    setReadSet(loadReadSet(storageKey));
  }, [storageKey]);

  const isUnread = React.useCallback((id: string) => !readSet.has(id), [readSet]);
  const markRead = React.useCallback(
    (id: string) => {
      if (!storageKey || readSet.has(id)) return;
      setReadSet((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        try {
          window.localStorage.setItem(storageKey, JSON.stringify([...next]));
        } catch {
          /* quota / private mode — drop silently */
        }
        return next;
      });
    },
    [readSet, storageKey],
  );

  return { isUnread, markRead };
}

function loadReadSet(storageKey: string | null): Set<string> {
  if (!storageKey) return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((s): s is string => typeof s === 'string'));
  } catch {
    return new Set();
  }
}

function StatusBadge({ status }: { status: TicketStatus }) {
  const styles: Record<TicketStatus, string> = {
    open: 'bg-primary/15 text-primary',
    'in-progress': 'bg-amber-500/15 text-amber-400',
    'waiting-customer': 'bg-purple-500/15 text-purple-400',
    deferred: 'bg-muted text-muted-foreground',
    resolved: 'bg-emerald-500/15 text-emerald-400',
    closed: 'bg-muted text-muted-foreground',
  };
  return (
    <span className={cn('rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider', styles[status])}>
      {STATUS_LABEL[status]}
    </span>
  );
}

